import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import {
  Box,
  Grid,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { grey, green } from "@mui/material/colors";
import { defaultStyle } from "components/Modals";
import { getProductionParkLimitations } from "api/resources/productionPark";
import { ProductionParkLimitationTypes } from "helpers/enums";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Table from "components/Table";

interface HistoryModalProps {
  productionParkId: number;
  productionParkType: string;
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
  dispatch: any;
  setSnackbar: any;
}

const HistoryModal = ({
  productionParkId,
  productionParkType,
}: HistoryModalProps) => {
  const [fromDate, setFromDate] = useState(dayjs().subtract(7, "day"));
  const [toDate, setToDate] = useState(dayjs().add(7, "day"));
  const [productionParkLimitations, setProductionParkLimitations] = useState(
    [],
  );

  useEffect(() => {
    getProductionParkLimitations(
      productionParkId,
      dayjs(fromDate).startOf("day").format("YYYY-MM-DDTHH:mm"),
      dayjs(toDate).endOf("day").format("YYYY-MM-DDTHH:mm"),
    ).then((result) => {
      setProductionParkLimitations(result.data);
    });
  }, [productionParkId, fromDate, toDate]);

  const columns = [
    {
      accessorKey: "changeType",
      header: "Operation",
    },
    {
      accessorKey: "toDatetime",
      header: "To",
      cell: (row: any) => {
        return `${dayjs(row.row.original.toDatetime).format(
          "DD.MM.YYYY HH:mm",
        )}`;
      },
    },
    {
      accessorKey: "modifiedAt",
      header: "Modified at",
      cell: (row: any) => {
        const time =
          row.row.original.changeType === "CREATED"
            ? row.row.original.createdAt
            : row.row.original.modifiedAt;

        return `${dayjs(time).format("DD.MM.YYYY HH:mm")}`;
      },
    },
    {
      accessorKey: "modifiedBy",
      header: "Modified by",
    },
  ];

  return (
    <Box sx={defaultStyle}>
      <Typography id="modal-modal-title" variant="h5" component="h5">
        Limitations history
      </Typography>
      <Typography
        id="modal-modal-title"
        component="p"
        sx={{ color: grey[700], mt: "0.6rem" }}
      >
        Set the date period to see limitations from the past.
      </Typography>
      <Grid container spacing={0}>
        <Grid item sm={12}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              mt: "2rem",
              mb: "2rem",
            }}
          >
            <DatePicker
              slotProps={{
                textField: { size: "small" },
                clearButton: { size: "small" },
              }}
              name="fromDate"
              value={fromDate}
              onChange={(value) => {
                setFromDate(dayjs(value));
              }}
              label="From date"
              sx={{ mr: "0.8rem" }}
            />
            <DatePicker
              slotProps={{
                textField: { size: "small" },
                clearButton: { size: "small" },
              }}
              name="toDate"
              value={toDate}
              onChange={(value) => {
                setToDate(dayjs(value));
              }}
              label="To date"
              sx={{ mr: "0.8rem" }}
            />
          </Box>
          <Box>
            <Grid item xs={12}>
              {productionParkLimitations.length > 0 && (
                <Grid
                  item
                  xs={12}
                  sx={{ overflow: "auto", maxHeight: "500px" }}
                >
                  {productionParkLimitations.map(
                    (limitation, limitationIndex) => {
                      const isActive =
                        dayjs(limitation.fromDateTime) <= dayjs() &&
                        dayjs(limitation.toDateTime) >= dayjs();

                      return (
                        <Accordion
                          sx={{ mb: "0.5rem" }}
                          key={"Limitation-" + limitationIndex}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                              backgroundColor: isActive
                                ? green[100]
                                : grey[200],
                              display: "flex",
                              justifyContent: "flex-start",
                              alignItems: "center",
                            }}
                          >
                            <Typography
                              component="p"
                              fontSize="0.9rem"
                              fontWeight="500"
                            >
                              {`${dayjs(limitation.fromDateTime).format("DD.MM.YYYY HH:mm")} - ${
                                limitation.toDateTime
                                  ? dayjs(limitation.toDateTime).format(
                                      "DD.MM.YYYY HH:mm",
                                    )
                                  : "..."
                              } | Type: ${ProductionParkLimitationTypes[limitation.type as keyof typeof ProductionParkLimitationTypes]} | Value: ${limitation.type === "MAX_OUTPUT" ? limitation.value : limitation.value * 100}${limitation.type === "MAX_OUTPUT" ? "kW" : "%"} ${productionParkType === "HYBRID" ? " | " + limitation.forecastType : ""}`}
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails sx={{ overflow: "hidden" }}>
                            <Typography
                              component="p"
                              fontSize="0.9rem"
                              fontWeight="500"
                              sx={{ mt: 1 }}
                            >
                              Change history:
                            </Typography>
                            <Table
                              dense
                              data={limitation.changeHistory}
                              isFetching={!limitation}
                              columns={columns}
                            />
                          </AccordionDetails>
                        </Accordion>
                      );
                    },
                  )}
                </Grid>
              )}
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default HistoryModal;
